import React from "react";
import styled from "styled-components";
import { color } from "styles/colours";
import { Image, Text, types, File, RichText, Repeater } from "react-bricks/frontend";
import { H2 } from "components/atoms/Heading";
import { P1 } from "components/atoms/Paragraph";
import { VideoPlayer } from "components/atoms/VideoPlayer";
import { mobile } from "components/templates/Breakpoints";
import { getVerticalPadding, VerticalPadding, verticalPadSideProp } from "../utils/verticalPadding";
import TextBlock from "../common/TextBlock";

export interface TextWithImageItemProps {
  reverse: boolean;
  eyebrow: boolean;
  footnote: boolean;
  useVideo: boolean;
  hideBody: boolean;
  eyebrowColor: types.IColor;
  paddingVertical?: VerticalPadding;
  backgroundColor: types.IColor;
}

export const TextWithImage: types.Brick<TextWithImageItemProps> = ({
  reverse = false,
  eyebrow = false,
  footnote = false,
  useVideo = false,
  hideBody = false,
  eyebrowColor,
  paddingVertical,
  backgroundColor,
}) => {
  return (
    <TextWithImageContainer
      reverse={reverse}
      pad={getVerticalPadding(paddingVertical)}
      backgroundColor={backgroundColor?.color}
    >
      <TextContainer>
        {eyebrow && (
          <Eyebrow color={eyebrowColor?.color}>
            <Text
              propName="eyebrowText"
              placeholder="Eyebrow"
              renderBlock={({ children }) => <>{children}</>}
            />
          </Eyebrow>
        )}
        <TextTitle>
          <Text
            propName="title"
            placeholder="TextWithImage Title"
            renderBlock={({ children }) => <>{children}</>}
          />
        </TextTitle>

        {!hideBody && <TextBlock propName="text" />}

        <Repeater
          propName="checklistItems"
          renderWrapper={(items) => <ChecklistWrapper>{items}</ChecklistWrapper>}
        />

        {footnote && (
          <Text
            propName="footnoteText"
            placeholder="This is where footnote text goes"
            renderBlock={({ children }) => <Footnote>{children}</Footnote>}
            renderPlaceholder={({ children }) => <Footnote>{children}</Footnote>}
          />
        )}
      </TextContainer>
      <ImageContainer>
        {useVideo ? (
          <File
            propName="videoUrl"
            allowedExtensions={[".mp4"]}
            renderBlock={(props) => (
              <VideoWrapper>
                <VideoPlayer mp4={props?.url} $maxWidth="544px" />
              </VideoWrapper>
            )}
          />
        ) : (
          <Image
            propName="image"
            alt={"image"}
            maxWidth={500}
            imageStyle={{ maxWidth: 500, borderRadius: 8 }}
          />
        )}
      </ImageContainer>
    </TextWithImageContainer>
  );
};

TextWithImage.schema = {
  name: "zigzag",
  label: "Text with Image",
  previewImageUrl: "/static/bricks/zigzag.png",
  getDefaultProps: () => ({
    title: "Small steps lead to a big change",
    text: "Lean on the principles of behavioural science to encourage simple everyday wellness activities, including 5-minute brisk walks and breathing exercises. These micro-interventions are the key to a healthy business.",
    reverse: false,
    verticalPadding: "s",
    eyebrow: true,
    eyebrowColor: { color: color.S3100 },
    eyebrowText: "Featured",
    backgroundColor: { color: color.white },
    image: {
      src: "https://assets.reactbricks.com/wqzbqeh9bMnAhYE/images/original/Au2Awpxyb4CFVLP/today-screen.webp",
    },
  }),
  repeaterItems: [
    {
      name: "checklistItems",
      itemType: "checklist-item",
      itemLabel: "Checklist Items",
      min: 0,
      max: 8,
    },
  ],
  sideEditProps: [
    {
      name: "reverse",
      label: "Reverse layout",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "useVideo",
      label: "Use Video",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "footnote",
      label: "Show Footnote",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "eyebrow",
      label: "Show Eyebrow",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "hideBody",
      label: "Hide body text?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "eyebrowColor",
      label: "Eyebrow Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      show: (props) => props.eyebrow,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { label: "purple", value: { color: color.S3100 } },
          { label: "blue", value: { color: color.S2100 } },
          { label: "pink", value: { color: color.P100 } },
          { label: "green", value: { color: "#28A87F" } },
        ],
      },
    },
    {
      name: "backgroundColor",
      label: "Background Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { value: { color: color.white }, label: "White" },
          { value: { color: color.B210 }, label: "Gray" },
        ],
      },
    },
    verticalPadSideProp,
  ],
};

const ZIG_ZAG_BREAKPOINT = `@media only screen and (max-width: 76rem)`;

const ChecklistWrapper = styled.div`
  margin-top: 1rem;
`;

const TextWithImageContainer = styled.div<{
  reverse: boolean;
  pad?: string;
  backgroundColor: string;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8rem;
  flex-direction: ${(p) => (p.reverse ? "row-reverse" : "row")};
  background-color: ${(p) => p.backgroundColor};

  ${({ pad }) => pad && `padding: ${pad} 0;`}

  ${ZIG_ZAG_BREAKPOINT} {
    flex-direction: column;
    gap: 3rem;
    align-items: center;
    padding: 0 2rem;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 28rem;

  ${ZIG_ZAG_BREAKPOINT} {
    width: 34rem;
  }

  ${mobile} {
    width: 100%;
  }
`;

const TextTitle = styled(H2)`
  font-size: 2.5rem;
  line-height: 2.625rem;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
  font-weight: 700;

  ${mobile} {
    font-size: 1.5rem;
    line-height: 2.25rem;
  }
`;

const ImageContainer = styled.div`
  width: 34rem;

  img {
    width: 100%;
    height: auto;
    display: block;
  }

  @media only screen and (max-width: 36rem) {
    width: 100%;
  }
`;

const VideoWrapper = styled.div`
  height: 30rem;
  width: 100%;
  background-color: ${color.B1100};
  border-radius: 0.5rem;
`;

const Footnote = styled(P1)`
  padding-top: 1.5rem;
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${color.B2100};
`;

const A = styled.a`
  color: ${color.P100};
  text-decoration: underline;
`;

const Eyebrow = styled.div<{
  color: string;
}>`
  font-weight: bold;
  font-size: 0.875rem;
  line-height: 1rem;
  color: ${(p) => p.color};
`;
